import React from 'react';
import { i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import optionTypes from '../../../constants/option-types';
import previewGifs from '../constants/preview-gifs';
import computeSpacingFallback from '../helpers/compute-spacing-fallback';

const { Paragraph, Icon } = designSystem;
const isMobile = bowser.mobile || bowser.tablet;

const SLIDER_MIN = 0;
const SLIDER_MAX = 128;
const SLIDER_STEP = 1;

const RESTRICTED_PADDING_VALUE = 32;

const PADDINGS = {
  top: {
    id: 'top-spacing',
    attributes: ['padding-top'],
    iconGlyph: 'padding-top',
  },
  bottom: {
    id: 'bottom-spacing',
    attributes: ['padding-bottom'],
    iconGlyph: 'padding-bottom',
  },
  left: {
    id: 'left-spacing',
    attributes: ['padding-left'],
    iconGlyph: 'padding-left',
  },
  right: {
    id: 'right-spacing',
    attributes: ['padding-right'],
    iconGlyph: 'padding-right',
  },
};

const getPopoverProps = ({ imgSrc, title, iconGlyph }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {title}. {i18next.t('Click the {icon}', { icon: '' })}{' '}
      <Icon type="white" size={16} strokeWidth={2} glyph={iconGlyph} insideText />
      {i18next.t('{click_the_icon} to adjust each one separately', { click_the_icon: '' })}
    </Paragraph>
  ),
});

const getValuePopoverProps = (restrictedValue) => ({
  renderContent: () => {
    const desktopMessage = i18next.t(
      'Note: Padding over {value} will be scaled down on tablets and mobile devices.',
      { value: `${restrictedValue}px` }
    );
    const mobileMessage = i18next.t('Note: Padding over {value} will only be shown on desktop.', {
      value: `${restrictedValue}px`,
    });

    return (
      <Paragraph appearance="white" size="small">
        {isMobile ? mobileMessage : desktopMessage}
      </Paragraph>
    );
  },
  checkVisibility: (value) => value > restrictedValue,
});

const getVerticalPaddings = () => ({
  id: 'vertical-spacing',
  title: i18next.t('Top&Bottom'),
  iconGlyph: 'padding-vertical',
  options: [PADDINGS.top, PADDINGS.bottom],
  popover: getPopoverProps({
    imgSrc: previewGifs.COLUMN_VERTICAL_PADDING_GIF,
    title: i18next.t('Adjust the top and bottom spaces inside columns'),
    iconGlyph: 'padding-vertical',
  }),
  valuePopover: getValuePopoverProps(RESTRICTED_PADDING_VALUE),
});

const getHorizontalPaddings = () => ({
  id: 'horizontal-spacing',
  title: i18next.t('Left&Right'),
  iconGlyph: 'padding-horizontal',
  options: [PADDINGS.left, PADDINGS.right],
  popover: getPopoverProps({
    imgSrc: previewGifs.COLUMN_HORIZONTAL_PADDING_GIF,
    title: i18next.t('Adjust the left and right spaces inside columns'),
    iconGlyph: 'padding-horizontal',
  }),
  valuePopover: getValuePopoverProps(RESTRICTED_PADDING_VALUE),
});

const getPaddings = (settings = {}) => {
  const paddingOptions = [getVerticalPaddings(), getHorizontalPaddings()];

  return paddingOptions.map((paddingOption) => ({
    ...settings,
    ...paddingOption,
    type: optionTypes.SLIDER,
    computeFallbackValue: computeSpacingFallback,
    mixedLabel: i18next.t('Mixed'),
    step: SLIDER_STEP,
    min: SLIDER_MIN,
    max: SLIDER_MAX,
    options: paddingOption.options.map((subOption) => ({
      ...settings,
      ...subOption,
      type: optionTypes.SLIDER,
      computeFallbackValue: computeSpacingFallback,
      valuePopover: getValuePopoverProps(RESTRICTED_PADDING_VALUE),
    })),
  }));
};

export { PADDINGS };
export default getPaddings;
